import React, { useContext, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import CommonInputField from "../common/CommonInputField";
import translations from "../common/Translations";
import AppContext from "../../context/AppContext";

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const validationSchema = Yup.object({
  advanceAmount: Yup.number()
    .required("Advance amount is required")
    .positive("Must be a positive number"),
  advanceDate: Yup.date().required(
    "Date to pay the advance amount is required"
  ),
  firstInstallmentDate: Yup.date().required(
    "Date for the first installment is required"
  ),
  numberOfMonths: Yup.number()
    .required("Number of months is required")
    .positive("Must be a positive number")
    .integer("Must be an integer"),
});

export const InstallmentPlan = () => {
  const formik = useFormik({
    initialValues: {
      advanceAmount: "",
      advanceDate: "",
      firstInstallmentDate: "",
      numberOfMonths: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const { advanceAmount, firstInstallmentDate, numberOfMonths } = values;
      const totalAmount = 10000;
      const remainingAmount = totalAmount - advanceAmount;
      const monthlyInstallment = remainingAmount / numberOfMonths;
      const installments = Array.from(
        { length: numberOfMonths },
        (_, index) => {
          const date = new Date(firstInstallmentDate);
          date.setMonth(date.getMonth() + index);
          return {
            date: date.toDateString(),
            amount: monthlyInstallment.toFixed(2),
          };
        }
      );
    },
  });
  const { langMode, theme } = useContext(AppContext);
  const todayPicker = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  return (
    <div>
      <div className="bg-white p-5">
        <div className="w-full max-w-md mx-auto">
          <form onSubmit={formik.handleSubmit}>
            <div className="flex flex-col gap-6">
              <div>
                <CommonInputField
                  label="Advance Amount"
                  name="advanceAmount"
                  type="number"
                  field={formik.getFieldProps('advanceAmount')}
                  form={formik}
                />
              </div>
              <div>
                <label className="block text-xs mb-1">
                  Date to Pay Advance Amount
                </label>
                <DatePicker
                  selected={parseYmd(formik.values.advanceDate)}
                  onChange={(date) => {
                    formik.setFieldValue("advanceDate", date ? formatYmd(date) : "");
                  }}
                  onBlur={() => formik.setFieldTouched("advanceDate", true)}
                  placeholderText="YYYY-MM-DD"
                  maxDate={todayPicker}
                  className={`w-full h-12 px-2 !rounded-lg text-sm border border-gray-300 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                  calendarClassName="!rounded-lg shadow-lg"
                  popperPlacement="bottom-start"
                  popperContainer={({ children }) => (
                    <div style={{ position: "fixed", zIndex: 100000 }}>{children}</div>
                  )}
                />
                {formik.touched.advanceDate && formik.errors.advanceDate ? (
                  <div className="text-red-600 text-xs mt-1">
                    {formik.errors.advanceDate}
                  </div>
                ) : null}
              </div>
              <div>
                <label className="block text-xs mb-1">
                  Date for the First Installment
                </label>
                <DatePicker
                  selected={parseYmd(formik.values.firstInstallmentDate)}
                  onChange={(date) => {
                    formik.setFieldValue("firstInstallmentDate", date ? formatYmd(date) : "");
                  }}
                  onBlur={() => formik.setFieldTouched("firstInstallmentDate", true)}
                  placeholderText="YYYY-MM-DD"
                  maxDate={todayPicker}
                  className={`w-full h-12 px-2 !rounded-lg text-sm border border-gray-300 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                  calendarClassName="!rounded-lg shadow-lg"
                  popperPlacement="bottom-start"
                  popperContainer={({ children }) => (
                    <div style={{ position: "fixed", zIndex: 100000 }}>{children}</div>
                  )}
                />
                {formik.touched.firstInstallmentDate &&
                  formik.errors.firstInstallmentDate ? (
                  <div className="text-red-600 text-xs mt-1">
                    {formik.errors.firstInstallmentDate}
                  </div>
                ) : null}
              </div>
              <div>
                <CommonInputField
                  label="Number of Months"
                  name="numberOfMonths"
                  type="number"
                  field={formik.getFieldProps('numberOfMonths')}
                  form={formik}
                />
              </div>
              <div className="pt-5 flex justify-end">
                <button data-button-intent="success"
                  type="submit"
                  className="px-4 py-2 text-white bg-blue-800 hover:bg-blue-900 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                >
                  {translations[langMode].submit}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
